// Draw boxes, highlights, arrows, freehand lines and numbered pins on a
// screenshot. The marks are flattened into the image on export, so what the
// reviewer sees is exactly what the submitter drew.
import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from "react";
import { compressCanvas } from "./captureScreen";

const TOOLS = [
  { id: "box", label: "▭", title: "Box" },
  { id: "highlight", label: "▮", title: "Highlight" },
  { id: "arrow", label: "↗", title: "Arrow" },
  { id: "pen", label: "✎", title: "Draw" },
  { id: "pin", label: "①", title: "Numbered pin" },
];
const COLORS = ["#ef4444", "#2563eb", "#16a34a", "#111827"];

function drawShape(ctx, s, scale) {
  const lw = Math.max(3, 3 * scale);
  ctx.strokeStyle = s.color;
  ctx.fillStyle = s.color;
  ctx.lineWidth = lw;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (s.type === "box") {
    ctx.strokeRect(s.x1, s.y1, s.x2 - s.x1, s.y2 - s.y1);
  } else if (s.type === "highlight") {
    ctx.save();
    ctx.globalAlpha = 0.3;
    ctx.fillStyle = "#facc15";
    ctx.fillRect(s.x1, s.y1, s.x2 - s.x1, s.y2 - s.y1);
    ctx.restore();
  } else if (s.type === "arrow") {
    const angle = Math.atan2(s.y2 - s.y1, s.x2 - s.x1);
    const head = 16 * scale;
    ctx.beginPath();
    ctx.moveTo(s.x1, s.y1);
    ctx.lineTo(s.x2, s.y2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(s.x2, s.y2);
    ctx.lineTo(s.x2 - head * Math.cos(angle - Math.PI / 6), s.y2 - head * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(s.x2 - head * Math.cos(angle + Math.PI / 6), s.y2 - head * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();
  } else if (s.type === "pen") {
    ctx.beginPath();
    s.points.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.stroke();
  } else if (s.type === "pin") {
    const r = 14 * scale;
    ctx.beginPath();
    ctx.arc(s.x1, s.y1, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = `bold ${Math.round(15 * scale)}px Inter, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(s.n), s.x1, s.y1 + 1);
  }
}

const Annotator = forwardRef(function Annotator({ src }, ref) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [shapes, setShapes] = useState([]);
  const [draft, setDraft] = useState(null);
  const [tool, setTool] = useState("box");
  const [color, setColor] = useState(COLORS[0]);
  const [loaded, setLoaded] = useState(false);

  // Marks are drawn in image pixels; `scale` keeps line widths readable on a
  // large screenshot shown small.
  const scaleFor = (canvas) => Math.max(1, canvas.width / 1000);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    const scale = scaleFor(canvas);
    [...shapes, ...(draft ? [draft] : [])].forEach((s) => drawShape(ctx, s, scale));
  }, [shapes, draft]);

  useEffect(() => {
    setLoaded(false);
    setShapes([]);
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
      }
      setLoaded(true);
    };
    img.src = src;
  }, [src]);

  useEffect(() => {
    if (loaded) redraw();
  }, [loaded, redraw]);

  useImperativeHandle(ref, () => ({
    // Unchanged screenshots go up as they came in; no re-encode.
    getImage: () => (shapes.length && canvasRef.current
      ? compressCanvas(canvasRef.current) : src),
  }), [shapes, src]);

  const point = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return [
      ((e.clientX - rect.left) / rect.width) * canvas.width,
      ((e.clientY - rect.top) / rect.height) * canvas.height,
    ];
  };

  const onPointerDown = (e) => {
    if (!loaded) return;
    const [x, y] = point(e);
    if (tool === "pin") {
      const n = shapes.filter((s) => s.type === "pin").length + 1;
      setShapes((prev) => [...prev, { type: "pin", x1: x, y1: y, n, color }]);
      return;
    }
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDraft(tool === "pen"
      ? { type: "pen", points: [[x, y]], color }
      : { type: tool, x1: x, y1: y, x2: x, y2: y, color });
  };

  const onPointerMove = (e) => {
    if (!draft) return;
    const [x, y] = point(e);
    setDraft((d) => (d.type === "pen"
      ? { ...d, points: [...d.points, [x, y]] }
      : { ...d, x2: x, y2: y }));
  };

  const onPointerUp = () => {
    if (!draft) return;
    const tiny = draft.type !== "pen"
      && Math.abs(draft.x2 - draft.x1) < 4 && Math.abs(draft.y2 - draft.y1) < 4;
    if (!tiny) setShapes((prev) => [...prev, draft]);
    setDraft(null);
  };

  const btn = (active) => ({
    border: "1px solid " + (active ? "#4f46e5" : "#e5e7eb"),
    background: active ? "#eef2ff" : "#fff",
    borderRadius: 8, minWidth: 34, height: 32, cursor: "pointer", fontSize: 15,
  });

  return (
    <div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
        {TOOLS.map((t) => (
          <button key={t.id} type="button" title={t.title} aria-label={t.title}
            aria-pressed={tool === t.id} onClick={() => setTool(t.id)} style={btn(tool === t.id)}>
            {t.label}
          </button>
        ))}
        <span style={{ width: 1, height: 24, background: "#e5e7eb", margin: "0 4px" }} />
        {COLORS.map((c) => (
          <button key={c} type="button" aria-label={`Colour ${c}`} aria-pressed={color === c}
            onClick={() => setColor(c)}
            style={{
              width: 22, height: 22, borderRadius: "50%", background: c, cursor: "pointer",
              border: color === c ? "3px solid #c7d2fe" : "2px solid #fff",
              boxShadow: "0 0 0 1px #d1d5db",
            }} />
        ))}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={() => setShapes((p) => p.slice(0, -1))}
          disabled={!shapes.length} style={btn(false)} title="Undo">↶ Undo</button>
        <button type="button" onClick={() => setShapes([])}
          disabled={!shapes.length} style={btn(false)} title="Clear all marks">Clear</button>
      </div>
      {/* Scrolls rather than letterboxing: a letterboxed canvas's bounding
          box no longer matches its pixels, and the marks land off-target. */}
      <div style={{ maxHeight: "55vh", overflow: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
      <canvas
        ref={canvasRef}
        data-testid="annotator-canvas"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          width: "100%", display: "block", cursor: "crosshair",
          touchAction: "none", background: "#f9fafb",
        }}
      />
      </div>
    </div>
  );
});

export default Annotator;
